import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Building2, ChevronRight, Loader2 } from "lucide-react";
import { useCompanySelector, UserCompany } from "@/hooks/useCompanySelector";
import { toast } from "sonner";

interface CompanySelectorModalProps {
  open: boolean;
  onSelected: () => void;
  onOpenChange?: (open: boolean) => void;
}

export const CompanySelectorModal = ({ open, onSelected, onOpenChange }: CompanySelectorModalProps) => {
  const { companies, setActiveCompany } = useCompanySelector();
  const [selecting, setSelecting] = useState<string | null>(null);

  const handleSelect = async (company: UserCompany) => {
    setSelecting(company.company_id);
    try {
      await setActiveCompany.mutateAsync(company.company_id);
      toast.success(`Switched to ${company.company_name}`);
      onSelected();
    } catch (err: any) {
      toast.error(err.message || "Failed to select company");
    } finally {
      setSelecting(null);
    }
  };

  const formatRole = (name: string) =>
    name.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

  return (
    <Dialog open={open} onOpenChange={onOpenChange ?? (() => {})}>
      <DialogContent className="sm:max-w-md" onPointerDownOutside={onOpenChange ? undefined : (e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-primary" />
            Select a Workspace
          </DialogTitle>
          <DialogDescription>
            Your account is linked to multiple companies. Choose which workspace to open.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 mt-2">
          {companies.map((company) => (
            <Button
              key={company.company_id}
              variant="outline"
              className="w-full justify-between h-auto py-3 px-4"
              disabled={selecting !== null}
              onClick={() => handleSelect(company)}
            >
              <div className="flex flex-col items-start gap-0.5">
                <span className="font-medium text-foreground">{company.company_name}</span>
                <span className="text-xs text-muted-foreground">{formatRole(company.role_name)}</span>
              </div>
              {selecting === company.company_id ? (
                <Loader2 className="h-4 w-4 animate-spin text-primary" />
              ) : (
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              )}
            </Button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};
